import React, { useState } from "react";
import { Combobox } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface ComboFieldItem {
  id: string | null;
  name: string;
}

interface ComboFieldProps {
  items: ComboFieldItem[];
  value: ComboFieldItem | null;
  onChange: (value: ComboFieldItem) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** When true, shows the options list as soon as the input is focused (before typing), using the same pattern as Autocomplete's initialSuggestionsCount. */
  showOptionsOnFocus?: boolean;
}

export default function ComboField({
  items,
  value,
  onChange,
  label,
  placeholder = "Select or type a value...",
  disabled = false,
  showOptionsOnFocus = false,
}: ComboFieldProps) {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const filteredItems =
    query === ""
      ? items
      : items.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));

  // Like Autocomplete initial suggestions: plain div instead of Combobox.Options so Headless UI
  // does not auto-activate the first option and commit it on blur.
  const showInitialSuggestions = showOptionsOnFocus && !disabled && isFocused && query === "";

  return (
    <Combobox
      as="div"
      value={value}
      onChange={(item: ComboFieldItem) => {
        setQuery("");
        onChange(item);
      }}
      disabled={disabled}
    >
      {label && (
        <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900">
          {label}
        </Combobox.Label>
      )}
      <div
        className="relative mt-1"
        onBlur={
          showOptionsOnFocus
            ? (e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setIsFocused(false);
                  setQuery("");
                }
              }
            : undefined
        }
      >
        <Combobox.Input
          className={clsx(
            "block w-full rounded-md py-1.5 pl-3 pr-10 text-sm leading-6 text-gray-900",
            "shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400",
            "focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none",
            "disabled:bg-neutral-50 disabled:text-gray-400 disabled:ring-gray-200",
          )}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          onBlur={showOptionsOnFocus ? undefined : () => setQuery("")}
          onFocus={showOptionsOnFocus && !disabled ? () => setIsFocused(true) : undefined}
          onKeyDown={
            showOptionsOnFocus && !disabled
              ? (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Escape") setIsFocused(false);
                }
              : undefined
          }
          displayValue={(item: ComboFieldItem | null) => item?.name ?? ""}
          placeholder={placeholder}
        />
        <Combobox.Button
          className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          onFocus={showOptionsOnFocus && !disabled ? () => setIsFocused(true) : undefined}
        >
          <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>

        {/* Unmount Combobox.Options while initial suggestions are visible (see Autocomplete). */}
        {showInitialSuggestions && (
          <div
            onMouseDown={(e) => e.preventDefault()}
            className={clsx(
              "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1",
              "text-sm shadow-lg ring-1 ring-black/5 focus:outline-none",
            )}
          >
            {items.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
            ) : (
              items.map((item) => {
                const selected = value != null && value.name === item.name && value.id === item.id;
                return (
                  <div
                    key={item.id != null ? String(item.id) : item.name}
                    className={clsx(
                      "group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900",
                      "hover:bg-primary-600 hover:text-white",
                    )}
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setIsFocused(false);
                      setQuery("");
                      onChange(item);
                    }}
                  >
                    <span className={clsx("block truncate", selected && "font-semibold")}>
                      {item.name}
                    </span>
                    {selected && (
                      <span
                        className={clsx(
                          "absolute inset-y-0 right-0 flex items-center pr-4",
                          "text-primary-600",
                          "group-hover:text-white",
                        )}
                      >
                        <CheckIcon className="size-5" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {!showInitialSuggestions && (
          <Combobox.Options
            className={clsx(
              "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1",
              "text-sm shadow-lg ring-1 ring-black/5 focus:outline-none",
            )}
          >
            {query.length > 0 && !filteredItems.some((item) => item.name === query) && (
              <Combobox.Option
                value={{ id: null, name: query }}
                className={({ active }) =>
                  clsx(
                    "relative cursor-default select-none py-2 pl-3 pr-9",
                    active ? "bg-primary-600 text-white" : "text-gray-900",
                  )
                }
              >
                {({ active }) => (
                  <span className={clsx("block truncate", active ? "text-white" : "text-gray-500")}>
                    Create &ldquo;{query}&rdquo;
                  </span>
                )}
              </Combobox.Option>
            )}

            {filteredItems.map((item) => (
              <Combobox.Option
                key={item.id}
                value={item}
                className={({ active }) =>
                  clsx(
                    "relative cursor-default select-none py-2 pl-3 pr-9",
                    active ? "bg-primary-600 text-white" : "text-gray-900",
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    <span className={clsx("block truncate", selected && "font-semibold")}>
                      {item.name}
                    </span>
                    {selected && (
                      <span
                        className={clsx(
                          "absolute inset-y-0 right-0 flex items-center pr-4",
                          active ? "text-white" : "text-primary-600",
                        )}
                      >
                        <CheckIcon className="size-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}

            {filteredItems.length === 0 && query.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
            )}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
